import { useEffect, useState } from 'react';

/**
 * Le champ de réponse au motif de refus d'un valideur.
 *
 * POURQUOI il est partagé entre les deux écrans de préparation (« Factures à
 * payer » et « Remboursements d'avoirs ») : ce n'est pas du JSX décoratif mais
 * une vraie logique — état local pendant la frappe, resynchronisation quand le
 * serveur renvoie autre chose (un nouveau cycle de refus efface la réponse), et
 * enregistrement à la SORTIE du champ, seulement si le texte a changé, pour ne
 * pas écrire à chaque caractère. Recopier cela dans le second écran l'aurait
 * fait diverger au premier correctif, et c'est exactement ce que la règle
 * anti-duplication du dépôt interdit.
 *
 * Il ne connaît NI `Paiement` NI `Remboursement` : une facture porte un
 * fournisseur, un avoir porte un client, et le champ n'a besoin ni de l'un ni de
 * l'autre. On lui passe donc une forme structurelle — la valeur, son
 * horodatage, le nom du valideur, et quoi faire à l'enregistrement.
 */
export function ReponseMotifRefus({
  reponse,
  reponseAt,
  valideur,
  onSave,
}: {
  /** La réponse telle qu'elle est ENREGISTRÉE côté serveur (null = aucune). */
  reponse?: string | null;
  /** Son horodatage, pour l'accusé de prise en compte. */
  reponseAt?: string | null;
  /** Nom du valideur qui recevra la ligne : celui qui a refusé, sauf changement. */
  valideur: string;
  onSave: (reponse: string | null) => void;
}) {
  const initial = reponse ?? '';
  const [value, setValue] = useState(initial);
  // Le parent se recharge après enregistrement : on resynchronise si la
  // réponse a changé côté serveur (ex. nouveau cycle de refus qui l'efface).
  useEffect(() => setValue(initial), [initial]);

  return (
    <div className="mt-1.5">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const v = value.trim();
          if (v !== initial.trim()) onSave(v || null);
        }}
        rows={2}
        placeholder={`Répondre à ${valideur}…`}
        className="w-full rounded border border-red-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {reponse && (
        <div className="text-[11px] text-slate-500 mt-0.5">
          ✓ Réponse enregistrée
          {reponseAt ? ` le ${new Date(reponseAt).toLocaleDateString('fr-FR')}` : ''}
          {` : elle sera transmise à ${valideur} à la re-soumission.`}
        </div>
      )}
    </div>
  );
}
